import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Container,
  Badge,
} from '@mui/material';
import {
  PhotoCamera as CameraIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  NavigateNext as NextIcon,
  Done as DoneIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchManual, uploadManualPages } from '../../store/slices/manualsSlice';
import { SimpleCameraCapture } from '../../components/mobile/SimpleCameraCapture';

interface PagePhoto {
  id: string;
  pageNumber: number;
  blob: Blob;
  preview: string;
  isUploaded: boolean;
  uploadError?: string;
}

export const ManualPhotoUploadPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { currentManual, isLoading } = useAppSelector((state) => state.manuals);
  const { user } = useAppSelector((state) => state.auth);

  // State for the guided capture process
  const [capturedPages, setCapturedPages] = useState<PagePhoto[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [showCamera, setShowCamera] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingPage, setEditingPage] = useState<number | null>(null);

  // Load manual data
  useEffect(() => {
    if (id) {
      dispatch(fetchManual(id));
    }
  }, [dispatch, id]);

  // Check permissions
  const canEdit = user?.role === 'admin' || user?.role === 'user';

  // Handle photo capture for a specific page
  const handlePhotoCapture = (imageBlob: Blob, imagePreview: string) => {
    const pageToEdit = editingPage || currentPageNumber;
    
    // Check if we're replacing an existing page
    const existingPageIndex = capturedPages.findIndex(p => p.pageNumber === pageToEdit);
    
    const newPhoto: PagePhoto = {
      id: `page-${pageToEdit}-${Date.now()}`,
      pageNumber: pageToEdit,
      blob: imageBlob,
      preview: imagePreview,
      isUploaded: false,
    };

    if (existingPageIndex >= 0) {
      // Replace existing page
      setCapturedPages(prev => {
        const updated = [...prev];
        updated[existingPageIndex] = newPhoto;
        return updated;
      });
      setSuccess(`Seite ${pageToEdit} wurde aktualisiert`);
    } else {
      // Add new page
      setCapturedPages(prev => [...prev, newPhoto].sort((a, b) => a.pageNumber - b.pageNumber));
      setSuccess(`Seite ${pageToEdit} wurde hinzugefügt`);
      
      // Move to next page if we were capturing sequentially
      if (!editingPage) {
        setCurrentPageNumber(prev => prev + 1);
      }
    }

    setShowCamera(false);
    setEditingPage(null);
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  };

  // Delete a captured page
  const handleDeletePage = (pageNumber: number) => {
    setCapturedPages(prev => prev.filter(p => p.pageNumber !== pageNumber));
    
    // Renumber remaining pages if needed
    setCapturedPages(prev => {
      const remaining = prev.filter(p => p.pageNumber !== pageNumber);
      return remaining.map((page, index) => ({
        ...page,
        pageNumber: index + 1
      }));
    });
    
    // Adjust current page number if needed
    if (currentPageNumber > capturedPages.length - 1) {
      setCurrentPageNumber(capturedPages.length);
    }
    
    setSuccess(`Seite ${pageNumber} wurde gelöscht`);
    setTimeout(() => setSuccess(null), 3000);
  };

  // Upload all captured pages to the server
  const handleUploadAll = async () => {
    if (!currentManual || capturedPages.length === 0) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Convert blobs to files
      const files = capturedPages.map(page => 
        new File([page.blob], `page-${page.pageNumber}.jpg`, { type: 'image/jpeg' })
      );

      // Upload all files
      const result = await dispatch(uploadManualPages({
        manualId: currentManual.id,
        files: files,
      }));

      if (uploadManualPages.fulfilled.match(result)) {
        setSuccess(`${capturedPages.length} Seiten wurden erfolgreich hochgeladen!`);
        
        // Mark all pages as uploaded
        setCapturedPages(prev => prev.map(page => ({
          ...page,
          isUploaded: true
        })));
        
        // Navigate to manual view after successful upload
        setTimeout(() => {
          navigate(`/manuals/${currentManual.id}`);
        }, 2000);
      } else {
        setError('Upload fehlgeschlagen. Bitte versuchen Sie es erneut.');
      }
    } catch (err) {
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Open camera for a specific page (edit mode)
  const handleEditPage = (pageNumber: number) => {
    setEditingPage(pageNumber);
    setShowCamera(true);
  };

  // Open camera for next page
  const handleCaptureNextPage = () => {
    setEditingPage(null);
    setShowCamera(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Anleitung wird geladen...
        </Typography>
      </Box>
    );
  }

  if (!currentManual) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Anleitung nicht gefunden
        </Typography>
        <Button onClick={() => navigate('/machines')} sx={{ mt: 2 }}>
          Zurück zu den Maschinen
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1">
              {currentManual.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Foto-Anleitung erstellen
            </Typography>
          </Box>
        </Box>

        {/* Status indicators */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${capturedPages.length} Seite${capturedPages.length !== 1 ? 'n' : ''} erfasst`}
            color={capturedPages.length > 0 ? "primary" : "default"}
            icon={<CameraIcon />}
          />
          {capturedPages.some(p => p.isUploaded) && (
            <Chip
              label={`${capturedPages.filter(p => p.isUploaded).length} hochgeladen`}
              color="success"
              icon={<DoneIcon />}
            />
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content Area */}
      <Grid container spacing={3}>
        {/* Left Side - Capture Guide */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Anleitung erfassen
            </Typography>
            
            <Stepper activeStep={currentPageNumber - 1} orientation="vertical">
              {[...Array(Math.max(currentPageNumber, capturedPages.length))].map((_, index) => {
                const pageNum = index + 1;
                const capturedPage = capturedPages.find(p => p.pageNumber === pageNum);
                
                return (
                  <Step key={pageNum} completed={!!capturedPage}>
                    <StepLabel>
                      Seite {pageNum}
                      {capturedPage && (
                        <Chip 
                          size="small" 
                          label="Erfasst" 
                          color="success" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </StepLabel>
                    <StepContent>
                      {capturedPage ? (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Diese Seite wurde bereits erfasst.
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditPage(pageNum)}
                          >
                            Erneut aufnehmen
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Fotografieren Sie Seite {pageNum} der Anleitung
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CameraIcon />}
                            onClick={handleCaptureNextPage}
                          >
                            Seite fotografieren
                          </Button>
                        </Box>
                      )}
                    </StepContent>
                  </Step>
                );
              })}
            </Stepper>

            {/* Add more pages button */}
            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<NextIcon />}
                onClick={() => {
                  setCurrentPageNumber(capturedPages.length + 1);
                  setShowCamera(true);
                }}
                disabled={isUploading}
              >
                Weitere Seite hinzufügen
              </Button>
            </Box>

            {/* Finish and upload button */}
            {capturedPages.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={isUploading ? <UploadIcon /> : <DoneIcon />}
                  onClick={handleUploadAll}
                  disabled={isUploading || capturedPages.every(p => p.isUploaded)}
                >
                  {isUploading ? 'Wird hochgeladen...' : 'Fertig - Alle Seiten hochladen'}
                </Button>
                {isUploading && (
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Side - Preview Grid */}
        <Grid size={{ xs: 12, md: 8 }}>
          {capturedPages.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50' }}>
              <CameraIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Noch keine Seiten erfasst
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Beginnen Sie mit dem Fotografieren der ersten Seite der Anleitung.
                Sie können jede Seite einzeln aufnehmen und bei Bedarf wiederholen.
              </Typography>
              <Button
                variant="contained"
                startIcon={<CameraIcon />}
                onClick={handleCaptureNextPage}
                size="large"
              >
                Erste Seite fotografieren
              </Button>
            </Paper>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Erfasste Seiten
              </Typography>
              <Grid container spacing={2}>
                {capturedPages.map((page) => (
                  <Grid size={{ xs: 6, sm: 4 }} key={page.id}>
                    <Card sx={{ position: 'relative' }}>
                      <Badge
                        badgeContent={`Seite ${page.pageNumber}`}
                        color="primary"
                        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                        sx={{
                          '& .MuiBadge-badge': {
                            top: 8,
                            left: 8,
                          }
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          image={page.preview}
                          alt={`Seite ${page.pageNumber}`}
                          sx={{ width: '100%' }}
                        />
                      </Badge>
                      
                      {/* Action buttons overlay */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          bgcolor: 'rgba(0, 0, 0, 0.7)',
                          p: 1,
                          display: 'flex',
                          justifyContent: 'space-around',
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleEditPage(page.pageNumber)}
                          sx={{ color: 'white' }}
                          disabled={isUploading}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePage(page.pageNumber)}
                          sx={{ color: 'white' }}
                          disabled={isUploading || page.isUploaded}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* Upload status indicator */}
                      {page.isUploaded && (
                        <Chip
                          label="Hochgeladen"
                          size="small"
                          color="success"
                          icon={<DoneIcon />}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                          }}
                        />
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Camera Dialog */}
      <SimpleCameraCapture
        open={showCamera}
        onClose={() => {
          setShowCamera(false);
          setEditingPage(null);
        }}
        onCapture={handlePhotoCapture}
        title={editingPage ? `Seite ${editingPage} erneut aufnehmen` : `Seite ${currentPageNumber} aufnehmen`}
      />
    </Container>
  );
};